import React from 'react';
import { Check, Package, Truck, Home } from 'lucide-react';

interface OrderStatusBarProps {
  status: string;
  trackingStatus?: string;
  isCompact?: boolean;
  createdAt?: string;
  etd?: string;
  awbCode?: string;
}

export const OrderStatusBar: React.FC<OrderStatusBarProps> = ({ 
  status, 
  trackingStatus, 
  isCompact = false,
  createdAt,
  etd,
  awbCode
}) => {
  if (status === 'failed') {
    return (
      <div className="flex items-center justify-center py-2 px-4 bg-red-50 text-red-600 rounded-lg border border-red-100">
        <span className="font-medium text-sm">Payment Failed / Order Cancelled</span>
      </div>
    );
  }

  if (status === 'created') {
    return (
      <div className="flex items-center justify-center py-2 px-4 bg-yellow-50 text-yellow-700 rounded-lg border border-yellow-100 animate-pulse">
        <span className="font-medium text-sm">Payment Incomplete / Pending Payment</span>
      </div>
    );
  }

  let currentStep = 0;
  if (trackingStatus) {
    const ts = trackingStatus.toUpperCase();
    if (ts.includes('DELIVERED')) {
      currentStep = 3;
    } else if (ts.includes('SHIP') || ts.includes('TRANSIT') || ts.includes('OUT FOR') || ts.includes('PICK')) {
      currentStep = 2;
    } else {
      currentStep = 1;
    }
  } else if (status === 'paid' || status === 'created') {
    currentStep = 0;
  }

  const formatDate = (d?: string) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : undefined;

  const steps = [
    { 
      label: 'Placed', 
      icon: <Check className="w-4 h-4 sm:w-5 sm:h-5" />,
      date: formatDate(createdAt),
      subtext: ''
    },
    { 
      label: 'Processing', 
      icon: <Package className="w-4 h-4 sm:w-5 sm:h-5" />,
      date: currentStep >= 1 ? 'Processed' : '',
      subtext: ''
    },
    { 
      label: 'Shipped', 
      icon: <Truck className="w-4 h-4 sm:w-5 sm:h-5" />,
      date: currentStep >= 2 ? 'In Transit' : '',
      subtext: currentStep >= 2 && awbCode ? `AWB: ${awbCode}` : ''
    },
    { 
      label: 'Delivered', 
      icon: <Home className="w-4 h-4 sm:w-5 sm:h-5" />,
      date: currentStep === 3 ? 'Delivered' : (etd ? `Expected by ${formatDate(etd)}` : ''),
      subtext: ''
    },
  ];

  return (
    <div className={`w-full ${isCompact ? 'py-3' : 'py-6'}`}>
      <div className="relative px-4 sm:px-8">
        {/* Background Line */}
        <div className="absolute left-12 right-12 sm:left-20 sm:right-20 top-4 sm:top-5 h-1 bg-gray-200 z-0">
          <div 
            className="absolute left-0 top-0 h-full bg-primary-600 transition-all duration-500"
            style={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
          ></div>
        </div>
        
        <div className="flex justify-between relative z-10">
          {steps.map((step, index) => {
            const isCompleted = index <= currentStep;
            const isActive = index === currentStep;
            
            return (
              <div key={index} className="flex flex-col items-center w-20 sm:w-32 relative">
                <div 
                  className={`w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full transition-all duration-300 ${
                    isCompleted 
                      ? 'bg-primary-600 text-white shadow-sm' 
                      : 'bg-white border-2 border-gray-200 text-gray-400'
                  } ${isActive ? 'ring-4 ring-primary-50 scale-110' : ''}`}
                >
                  {step.icon}
                </div>
                {!isCompact && (
                  <div className="mt-3 flex flex-col items-center text-center">
                    <span className={`text-xs sm:text-sm font-semibold transition-colors duration-300 ${
                      isActive ? 'text-primary-700' : isCompleted ? 'text-gray-800' : 'text-gray-400'
                    }`}>
                      {step.label}
                    </span>
                    {step.date && (
                      <span className={`text-[10px] sm:text-xs mt-1 font-medium ${isCompleted ? 'text-gray-600' : 'text-gray-400'}`}>
                        {step.date}
                      </span>
                    )}
                    {step.subtext && (
                      <span className="text-[10px] sm:text-[11px] text-primary-600 font-medium mt-0.5 max-w-full overflow-hidden text-ellipsis px-1">
                        {step.subtext}
                      </span>
                    )}
                  </div>
                )}
                {isCompact && isActive && (
                  <div className="mt-1 text-[10px] sm:text-xs font-bold text-primary-700 text-center absolute -bottom-5 whitespace-nowrap">
                    {step.label}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Tracking Status Text Display */}
      {trackingStatus && !isCompact && (
        <div className="text-center mt-6 text-sm text-gray-500 font-medium">
          Current Status: <span className="text-gray-900">{trackingStatus}</span>
        </div>
      )}
    </div>
  );
};
